import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Mail, UserPlus, X, Award } from "lucide-react";
import { api } from "./api";
import { useAuth } from "./auth";
import { useToast } from "./toast";
import { Spinner } from "@/components/app/Spinner";

const PendingCtx = createContext(null);
const POLL_MS = 30_000;
const EMPTY = { to_accept: [], to_approve: [] };

export function PendingApprovalsProvider({ children }) {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [pending, setPending] = useState(EMPTY);
  const [seenIds, setSeenIds] = useState(new Set());
  const [dismissedIds, setDismissedIds] = useState(new Set());
  const [visible, setVisible] = useState(false);
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) return;
    try { setPending(await api("/members/pending")); } catch { /* silent */ }
  }, [user]);

  useEffect(() => {
    if (!user) {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      setPending(EMPTY); setSeenIds(new Set()); setDismissedIds(new Set()); setVisible(false); setCurrent(null);
      return undefined;
    }
    refresh();
    timerRef.current = setInterval(refresh, POLL_MS);
    const onVis = () => {
      if (document.visibilityState === "visible") {
        refresh();
        if (!timerRef.current) timerRef.current = setInterval(refresh, POLL_MS);
      } else if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => { if (timerRef.current) clearInterval(timerRef.current); timerRef.current = null; document.removeEventListener("visibilitychange", onVis); };
  }, [user, refresh]);

  useEffect(() => {
    if (!user) return;
    const fresh = (m) => !seenIds.has(m.id) && !dismissedIds.has(m.id);
    const a = pending.to_approve.find(fresh);
    if (a) { setCurrent({ kind: "approve", item: a }); setSeenIds((s) => new Set(s).add(a.id)); setVisible(true); return; }
    const b = pending.to_accept.find(fresh);
    if (b) { setCurrent({ kind: "accept", item: b }); setSeenIds((s) => new Set(s).add(b.id)); setVisible(true); }
  }, [pending, seenIds, dismissedIds, user]);

  const closeModal = useCallback(() => { setVisible(false); setTimeout(() => setCurrent(null), 250); }, []);

  const wrap = useCallback((fn, ok, kind, fail) => async (...args) => {
    setBusy(true);
    try { await fn(...args); toast.show(ok, kind); await refresh(); }
    catch (e) { toast.show(e instanceof Error ? e.message : fail, "error"); }
    finally { setBusy(false); }
  }, [refresh, toast]);

  const approve = useMemo(() => wrap((id, role = "employee") => api(`/members/${id}/approve`, { method: "POST", body: { role } }), "Member approved", "success", "Approve failed"), [wrap]);
  const reject = useMemo(() => wrap((id) => api(`/members/${id}/reject`, { method: "POST" }), "Request rejected", "info", "Reject failed"), [wrap]);
  const accept = useMemo(() => wrap((id) => api(`/members/${id}/accept`, { method: "POST" }), "Invite accepted. Awaiting manager approval.", "info", "Accept failed"), [wrap]);
  const decline = useMemo(() => wrap((id) => api(`/members/${id}/reject`, { method: "POST" }), "Invitation declined", "info", "Decline failed"), [wrap]);

  const onPrimary = async () => { if (!current) return; if (current.kind === "approve") await approve(current.item.id, "employee"); else await accept(current.item.id); closeModal(); };
  const onSecondary = async () => { if (!current) return; if (current.kind === "approve") await reject(current.item.id); else await decline(current.item.id); closeModal(); };
  const onDismiss = () => { if (current) setDismissedIds((s) => new Set(s).add(current.item.id)); closeModal(); };

  const value = useMemo(() => ({
    toApprove: pending.to_approve, toAccept: pending.to_accept,
    count: pending.to_approve.length + pending.to_accept.length,
    refresh, approve, reject, accept, decline,
  }), [pending, refresh, approve, reject, accept, decline]);

  const isApprove = current?.kind === "approve";
  const personLine = current?.item.full_name || current?.item.email || "";
  const wsLine = current?.item.workspace_name || "your workspace";

  return (
    <PendingCtx.Provider value={value}>
      {children}
      {visible && current ? (
        <div className="fade-in fixed inset-0 z-[8000] flex items-end justify-center bg-black/50 sm:items-center" onClick={onDismiss}>
          <div
            className="sheet-in w-full max-w-lg space-y-3 rounded-t-[22px] border-t border-a-border bg-a-surface p-[18px] pb-7 sm:rounded-[22px] sm:border"
            onClick={(e) => e.stopPropagation()}
            data-testid="pending-approval-modal"
          >
            <div className="mx-auto mb-1.5 h-1 w-9 rounded bg-black/15 sm:hidden" />
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-a-dim text-a-primary">
                {isApprove ? <UserPlus size={20} /> : <Mail size={20} />}
              </div>
              <div className="flex-1">
                <p className="text-[11px] font-extrabold uppercase tracking-[1.2px] text-a-muted">{isApprove ? "Manager action" : "Invitation"}</p>
                <p className="text-[17px] font-extrabold text-a-text" data-testid="pending-approval-title">{isApprove ? "New join request" : "Workspace invitation"}</p>
              </div>
              <button onClick={onDismiss} className="text-a-muted hover:text-a-text" data-testid="pending-approval-dismiss"><X size={22} /></button>
            </div>
            <div className="space-y-1 rounded-[14px] border border-a-border bg-a-bg p-3.5">
              <p className="truncate text-base font-extrabold text-a-text" data-testid="pending-approval-person">{personLine}</p>
              {current.item.email && current.item.email !== personLine ? <p className="truncate text-xs text-a-muted">{current.item.email}</p> : null}
              <p className="mt-1.5 text-[13px] text-a-muted">{isApprove ? `wants to join ${wsLine}` : `You've been invited to ${wsLine}`}</p>
              {current.item.role ? (
                <span className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-a-dim px-2.5 py-1 text-xs font-bold text-a-primary"><Award size={12} />Role: {current.item.role}</span>
              ) : null}
            </div>
            <div className="mt-1 flex gap-2.5">
              <button onClick={onSecondary} disabled={busy} className="btn-press h-12 flex-1 rounded-xl border border-a-border bg-a-surface font-extrabold text-a-danger disabled:opacity-60" data-testid="pending-approval-secondary">
                {isApprove ? "Reject" : "Decline"}
              </button>
              <button onClick={onPrimary} disabled={busy} className="btn-press flex h-12 flex-1 items-center justify-center rounded-xl bg-a-primary font-extrabold text-white disabled:opacity-60" data-testid="pending-approval-primary">
                {busy ? <Spinner size={18} color="#fff" /> : isApprove ? "Approve" : "Accept"}
              </button>
            </div>
            <button onClick={() => { onDismiss(); navigate("/workspaces"); }} className="mx-auto mt-1.5 flex items-center gap-1 px-2 py-1.5 font-bold text-a-primary" data-testid="pending-approval-view-all">
              View all in Workspaces <ChevronRight size={16} />
            </button>
          </div>
        </div>
      ) : null}
    </PendingCtx.Provider>
  );
}

export function usePendingApprovals() {
  const v = useContext(PendingCtx);
  if (!v) throw new Error("usePendingApprovals must be used inside PendingApprovalsProvider");
  return v;
}
